/**
 * OrderDetails Component
 *
 * Displays detailed information for a specific order fetched from the API.
 * Includes order tracking timeline, pickup/dropoff details, and cancel functionality.
 *
 * Uses:
 * - useCustomerOrder hook to fetch order details
 * - useCustomerOrderTracking hook to fetch tracking timeline
 * - useCancelCustomerOrder mutation for cancellation
 * - useAuthConfig hook for authentication
 */

import React, { useState } from 'react';
import {
  ArrowLeft,
  MapPin,
  Phone,
  User,
  CheckCircle2,
  Circle,
  Package,
  Truck,
  Navigation,
  CreditCard,
  Calendar,
  RefreshCw,
  XCircle,
  AlertTriangle,
  X,
  Loader2,
  Download,
} from 'lucide-react';
import {
  useCustomerOrder,
  useCustomerOrderTracking,
  useCancelCustomerOrder,
  type CustomerOrder,
  type CustomerOrderStatus,
} from '@cfleet/api-client';
import { useAuthConfig } from '@/hooks/useAuthConfig';

interface OrderDetailsProps {
  orderId: string;
  onBack: () => void;
}

const ORDER_STEPS: CustomerOrderStatus[] = ['Draft', 'Placed', 'In Transit', 'Completed'];

interface TimelineStepProps {
  step: string;
  index: number;
  current: number;
  isLast: boolean;
}

const TimelineStep: React.FC<TimelineStepProps> = ({ step, index, current, isLast }) => {
  const isCompleted = index < current;
  const isCurrent = index === current;

  return (
    <div className="flex gap-4">
      <div className="flex flex-col items-center">
        <div
          className={`
          w-6 h-6 rounded-full flex items-center justify-center border transition-colors z-10
          ${
            isCompleted || isCurrent
              ? 'bg-blue-600 border-blue-600 text-white'
              : 'bg-white border-zinc-300 text-zinc-300'
          }
        `}
        >
          {isCompleted ? (
            <CheckCircle2 size={14} />
          ) : (
            <Circle size={10} fill={isCurrent ? 'currentColor' : 'none'} />
          )}
        </div>
        {!isLast && (
          <div className={`w-px flex-1 my-1 ${isCompleted ? 'bg-blue-600' : 'bg-zinc-200'}`}></div>
        )}
      </div>
      <div className={`pb-8 ${isCurrent ? 'text-zinc-900' : 'text-zinc-500'}`}>
        <div className="text-sm font-medium">{step}</div>
        {isCurrent && (
          <div className="text-xs text-blue-600 mt-0.5 animate-pulse">Processing...</div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Loading Skeleton
// ─────────────────────────────────────────────────────────────────────────────

const OrderDetailsSkeleton: React.FC = () => (
  <div data-testid="order-details-loading" className="flex flex-col h-full bg-white">
    <div className="h-14 border-b border-zinc-200 flex items-center justify-between px-6 bg-white">
      <div className="flex items-center gap-3">
        <div className="h-6 w-6 bg-zinc-200 rounded animate-pulse" />
        <div className="h-4 w-32 bg-zinc-200 rounded animate-pulse" />
      </div>
      <div className="flex items-center gap-4">
        <div className="h-4 w-24 bg-zinc-200 rounded animate-pulse" />
        <div className="h-4 w-20 bg-zinc-200 rounded animate-pulse" />
      </div>
    </div>
    <div className="flex-1 p-6">
      <div className="max-w-3xl mx-auto">
        <div className="w-full h-64 bg-zinc-100 rounded-lg mb-8 animate-pulse" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="flex gap-4">
                <div className="w-6 h-6 bg-zinc-200 rounded-full animate-pulse" />
                <div className="h-4 w-20 bg-zinc-200 rounded animate-pulse" />
              </div>
            ))}
          </div>
          <div className="md:col-span-2 space-y-8">
            <div className="p-4 border border-zinc-200 rounded-lg space-y-3">
              <div className="h-4 w-full bg-zinc-200 rounded animate-pulse" />
              <div className="h-4 w-3/4 bg-zinc-200 rounded animate-pulse" />
            </div>
            <div className="p-4 border border-zinc-200 rounded-lg space-y-3">
              <div className="h-4 w-full bg-zinc-200 rounded animate-pulse" />
              <div className="h-4 w-3/4 bg-zinc-200 rounded animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Error State
// ─────────────────────────────────────────────────────────────────────────────

interface ErrorStateProps {
  onRetry: () => void;
  onBack: () => void;
}

const ErrorState: React.FC<ErrorStateProps> = ({ onRetry, onBack }) => (
  <div className="flex flex-col items-center justify-center h-full bg-white p-8">
    <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-4">
      <XCircle className="w-8 h-8 text-red-500" />
    </div>
    <h3 className="text-lg font-semibold text-zinc-900 mb-2">Failed to Load Order</h3>
    <p className="text-sm text-zinc-500 mb-6 text-center max-w-sm">
      We couldn't load the order details. Please check your connection and try again.
    </p>
    <div className="flex items-center gap-3">
      <button
        onClick={onBack}
        className="px-4 py-2 text-sm font-medium text-zinc-600 bg-white border border-zinc-200 rounded-lg hover:bg-zinc-50 transition-colors"
      >
        Go Back
      </button>
      <button
        onClick={onRetry}
        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
      >
        <RefreshCw size={16} />
        Retry
      </button>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Cancel Order Modal
// ─────────────────────────────────────────────────────────────────────────────

interface CancelModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  isPending: boolean;
}

const CancelModal: React.FC<CancelModalProps> = ({ isOpen, onClose, onConfirm, isPending }) => {
  const [reason, setReason] = useState('');

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-zinc-400 hover:text-zinc-600"
        >
          <X size={20} />
        </button>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-red-600" />
          </div>
          <h3 className="text-lg font-semibold text-zinc-900">Cancel Order</h3>
        </div>
        <p className="text-sm text-zinc-600 mb-4">
          Are you sure you want to cancel this order? This action cannot be undone.
        </p>
        <div className="mb-4">
          <label className="block text-sm font-medium text-zinc-700 mb-1">
            Reason for cancellation (optional)
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Let us know why you're cancelling..."
            className="w-full px-3 py-2 border border-zinc-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            rows={3}
          />
        </div>
        <div className="flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isPending}
            className="px-4 py-2 text-sm font-medium text-zinc-600 bg-white border border-zinc-200 rounded-lg hover:bg-zinc-50 transition-colors disabled:opacity-50"
          >
            Keep Order
          </button>
          <button
            onClick={() => onConfirm(reason)}
            disabled={isPending}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
          >
            {isPending ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Cancelling...
              </>
            ) : (
              'Cancel Order'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main OrderDetails Component
// ─────────────────────────────────────────────────────────────────────────────

export const OrderDetails: React.FC<OrderDetailsProps> = ({ orderId, onBack }) => {
  const authConfig = useAuthConfig();
  const [showCancelModal, setShowCancelModal] = useState(false);

  // Fetch order details
  const { data: order, isLoading, isError, refetch } = useCustomerOrder(orderId, { authConfig });

  // Fetch tracking timeline (optional enhancement)
  const { data: tracking } = useCustomerOrderTracking(orderId, { authConfig });

  // Cancel order mutation
  const cancelMutation = useCancelCustomerOrder({ authConfig });

  const handleCancelOrder = (reason: string) => {
    cancelMutation.mutate(
      { orderId, reason: reason || undefined },
      {
        onSuccess: () => {
          setShowCancelModal(false);
          // Refetch order to show updated status
          refetch();
        },
        onError: (error) => {
          // Handle error - could show toast notification
          console.error('Failed to cancel order:', error);
          alert(error.message || 'Failed to cancel order');
        },
      }
    );
  };

  // Loading state
  if (isLoading) {
    return <OrderDetailsSkeleton />;
  }

  // Error state
  if (isError || !order) {
    return <ErrorState onRetry={() => refetch()} onBack={onBack} />;
  }

  const currentStepIndex = ORDER_STEPS.indexOf(order.status);
  const isCancelled = order.status === 'Cancelled';
  const canCancel = order.status === 'Draft' || order.status === 'Placed';
  const canDownloadPOD = order.status === 'Completed';

  // Format date - handle both snake_case and camelCase for compatibility
  const createdAt = (order as any).created_at || (order as any).createdAt;
  const formattedDate = createdAt ? new Date(createdAt).toLocaleDateString() : 'N/A';

  return (
    <div className="flex flex-col h-full bg-white text-zinc-900">
      {/* Header */}
      <div className="h-14 border-b border-zinc-200 flex items-center justify-between px-6 bg-white sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="text-zinc-500 hover:text-zinc-800 transition-colors p-1 hover:bg-zinc-100 rounded"
          >
            <ArrowLeft size={16} />
          </button>
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold text-zinc-900">Order #{order.id}</h2>
              <span
                className={`px-1.5 py-0.5 text-[10px] font-medium rounded border ${
                  isCancelled
                    ? 'bg-red-50 text-red-600 border-red-200'
                    : 'bg-blue-50 text-blue-600 border-blue-200'
                }`}
              >
                {order.status}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4 text-xs">
          <div className="flex items-center gap-1.5 text-zinc-500">
            <Calendar size={14} />
            <span>{formattedDate}</span>
          </div>
          <div className="h-4 w-px bg-zinc-200"></div>
          <div className="flex items-center gap-1.5 text-zinc-900 font-mono">
            <CreditCard size={14} className="text-zinc-400" />
            <span>${order.price.toFixed(2)}</span>
          </div>
          {canCancel && (
            <>
              <div className="h-4 w-px bg-zinc-200"></div>
              <button
                onClick={() => setShowCancelModal(true)}
                className="px-2.5 py-1 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded hover:bg-red-100 transition-colors"
              >
                Cancel Order
              </button>
            </>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto custom-scrollbar p-6">
        <div className="max-w-3xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Left Column: Timeline */}
            <div className="md:col-span-1">
              <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-4 flex items-center gap-2">
                <Truck size={14} />
                Tracking
              </h3>
              <div className="pl-1">
                {isCancelled ? (
                  <div className="flex gap-4">
                    <div className="w-6 h-6 rounded-full bg-red-50 border border-red-200 text-red-500 flex items-center justify-center">
                      <Navigation size={12} />
                    </div>
                    <div className="text-red-600 text-sm font-medium pt-0.5">Order Cancelled</div>
                  </div>
                ) : (
                  ORDER_STEPS.map((step, idx) => (
                    <TimelineStep
                      key={step}
                      step={step}
                      index={idx}
                      current={currentStepIndex}
                      isLast={idx === ORDER_STEPS.length - 1}
                    />
                  ))
                )}
              </div>
            </div>

            {/* Right Column: Details */}
            <div className="md:col-span-2 space-y-8">
              {/* Pickup Card */}
              <div>
                <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-3 ml-1">
                  Pickup
                </h3>
                <div className="bg-white border border-zinc-200 rounded-lg p-4 hover:border-zinc-300 transition-colors shadow-sm">
                  <div className="flex items-start gap-3 mb-3">
                    <MapPin className="text-zinc-500 mt-0.5" size={16} />
                    <div>
                      <div className="text-sm font-medium text-zinc-900">
                        {order.pickup.address.street}
                      </div>
                      <div className="text-xs text-zinc-500">
                        {order.pickup.address.city}, {order.pickup.address.zip}
                      </div>
                      {order.pickup.notes && (
                        <div className="mt-2 text-xs bg-zinc-50 p-2 rounded text-zinc-600 border border-zinc-100">
                          Note: {order.pickup.notes}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-6 pl-7">
                    <div className="flex items-center gap-2 text-xs text-zinc-500">
                      <User size={12} />
                      {order.pickup.contact.name}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-zinc-500">
                      <Phone size={12} />
                      {order.pickup.contact.phone}
                    </div>
                  </div>
                </div>
              </div>

              {/* Dropoff Card */}
              <div>
                <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-3 ml-1">
                  Dropoff
                </h3>
                <div className="bg-white border border-zinc-200 rounded-lg p-4 hover:border-zinc-300 transition-colors shadow-sm">
                  <div className="flex items-start gap-3 mb-3">
                    <MapPin className="text-blue-500 mt-0.5" size={16} />
                    <div>
                      <div className="text-sm font-medium text-zinc-900">
                        {order.dropoff.address.street}
                      </div>
                      <div className="text-xs text-zinc-500">
                        {order.dropoff.address.city}, {order.dropoff.address.zip}
                      </div>
                      {order.dropoff.notes && (
                        <div className="mt-2 text-xs bg-zinc-50 p-2 rounded text-zinc-600 border border-zinc-100">
                          Note: {order.dropoff.notes}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-6 pl-7">
                    <div className="flex items-center gap-2 text-xs text-zinc-500">
                      <User size={12} />
                      {order.dropoff.contact.name}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-zinc-500">
                      <Phone size={12} />
                      {order.dropoff.contact.phone}
                    </div>
                  </div>
                </div>
              </div>

              {/* Items Card (if available) */}
              {order.items && (
                <div>
                  <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-3 ml-1">
                    Package Details
                  </h3>
                  <div className="bg-white border border-zinc-200 rounded-lg p-4 shadow-sm">
                    <div className="flex items-start gap-3">
                      <Package className="text-zinc-500 mt-0.5" size={16} />
                      <div className="space-y-1">
                        {order.items.description && (
                          <div className="text-sm text-zinc-900">{order.items.description}</div>
                        )}
                        <div className="flex gap-4 text-xs text-zinc-500">
                          {order.items.quantity && <span>Qty: {order.items.quantity}</span>}
                          {order.items.weight && <span>Weight: {order.items.weight}</span>}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="pt-4 border-t border-zinc-100 flex justify-between items-center">
                <div className="text-xs text-zinc-500">
                  Carrier:{' '}
                  <span className="text-zinc-700 font-medium">cfleet Courier Services</span>
                </div>
                {canDownloadPOD && (
                  <button className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1 font-medium">
                    <Download size={12} />
                    Download Proof of Delivery
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Cancel Modal */}
      <CancelModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirm={handleCancelOrder}
        isPending={cancelMutation.isPending}
      />
    </div>
  );
};
